import React from "react";
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import EyeIcon from "../../../assets/eyeIcon";
import EyeOffIcon from "../../../assets/eyeOffIcon";

const INK = "#12141A";
const MUTED = "#6A7085";
const LINE = "#E3E6ED";

const KEYPAD_ROWS: (string | null)[][] = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [null, "0", "backspace"],
];

const MOBILE_NUMBER_MAX_LENGTH = 10;

/**
 * On web, a flex:1 chain only fills the viewport if some ancestor already
 * has a resolved height — not guaranteed when this screen is previewed/
 * mounted standalone. Force full-viewport height there; native ignores
 * 'vh' units, so it stays a no-op on mobile.
 */
const screenFillStyle: any =
  Platform.OS === "web" ? { minHeight: "100vh" } : null;

/** Suppresses the default browser focus ring on web — this is a touchscreen
 * kiosk display, not a keyboard-navigated page. No-ops on native. */
const webNoOutlineStyle: any =
  Platform.OS === "web" ? { outlineStyle: "none" } : null;

export interface MobileNumberEntryScreenProps {
  mobileNumber: string;
  onChangeMobileNumber: (value: string) => void;
  isMasked: boolean;
  onToggleMasked: () => void;
  onContinue: (number: any) => void;
  logoUrl?: string;
}

/**
 * Landing screen shown before the cart/payment view — collects the
 * customer's mobile number via an on-screen keypad (this is a secondary
 * customer-facing monitor, so there's no physical keyboard to type with).
 */
export function MobileNumberEntryScreen({
  mobileNumber,
  onChangeMobileNumber,
  isMasked,
  onToggleMasked,
  onContinue,
  logoUrl,
}: MobileNumberEntryScreenProps) {
  const handleKeyPress = (key: string) => {
    if (key === "backspace") {
      onChangeMobileNumber(mobileNumber.slice(0, -1));
      return;
    }

    if (mobileNumber.length >= MOBILE_NUMBER_MAX_LENGTH) {
      return;
    }

    onChangeMobileNumber(mobileNumber + key);
  };

  const displayValue = isMasked
    ? "•".repeat(mobileNumber.length)
    : mobileNumber;

  return (
    <View style={[styles.screen, screenFillStyle]}>
      <View style={styles.layout}>
        <View style={styles.leftPanel}>
          {!!logoUrl && (
            <Image
              source={{ uri: logoUrl }}
              style={styles.logo}
              resizeMode="contain"
            />
          )}

          <Text style={styles.heading}>
            Enter your mobile number
          </Text>

          <Text style={styles.subtext}>
            Get your digital bill on this number.
          </Text>
        </View>

        <View style={styles.rightPanel}>
          <View style={styles.inputRow}>
            <Text style={styles.countryCode}>+91</Text>

            <Text style={styles.value} numberOfLines={1}>
              {displayValue}
            </Text>

            <Pressable
              style={[styles.eyeToggle, webNoOutlineStyle]}
              onPress={onToggleMasked}
              hitSlop={12}
            >
              {isMasked ? (
                <EyeOffIcon color={MUTED} width={22} height={22} />
              ) : (
                <EyeIcon color={INK} width={22} height={22} />
              )}
            </Pressable>
          </View>

          <View style={styles.keypad}>
            {KEYPAD_ROWS.map((row, rowIndex) => (
              <View style={styles.keypadRow} key={`row-${rowIndex}`}>
                {row.map((key, keyIndex) =>
                  key === null ? (
                    <View
                      style={styles.keySpacer}
                      key={`spacer-${rowIndex}-${keyIndex}`}
                    />
                  ) : (
                    <Pressable
                      style={[styles.key, webNoOutlineStyle]}
                      key={key}
                      onPress={() => handleKeyPress(key)}
                    >
                      <Text style={styles.keyText}>
                        {key === "backspace" ? "⌫" : key}
                      </Text>
                    </Pressable>
                  ),
                )}
              </View>
            ))}
          </View>

          <Pressable
            style={[styles.continueButton, webNoOutlineStyle]}
            onPress={() => onContinue(mobileNumber)}
          >
            <Text style={styles.continueText}>Continue</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },

  layout: {
    flex: 1,
    flexDirection: "row",
  },

  leftPanel: {
    flex: 1,
    justifyContent: "center",
    backgroundColor: "#E9EBF6",
    paddingHorizontal: 48,
  },

  logo: {
    width: 64,
    height: 64,
    marginBottom: 24,
  },

  heading: {
    color: INK,
    fontSize: 34,
    fontWeight: "700",
    marginBottom: 12,
  },

  subtext: {
    color: MUTED,
    fontSize: 18,
  },

  rightPanel: {
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 60,
    backgroundColor: "#FFFFFF",
  },

  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 2,
    borderColor: INK,
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 24,
  },

  countryCode: {
    color: MUTED,
    fontSize: 20,
    marginRight: 12,
  },

  value: {
    flex: 1,
    color: INK,
    fontSize: 20,
    letterSpacing: 2,
  },

  eyeToggle: {
    padding: 4,
  },

  keypad: {
    marginTop: 24,
  },

  keypadRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "52%",
    height: "auto",
    alignSelf: "center",
    marginBottom: 16,
  },

  key: {
    width: 84,
    height: 64,
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },

  keySpacer: {
    width: 84,
    height: 64,
  },

  keyText: {
    color: INK,
    fontSize: 22,
    fontWeight: "600",
  },

  continueButton: {
    backgroundColor: INK,
    borderRadius: 16,
    paddingVertical: 18,
    alignItems: "center",
    justifyContent: "center",
  },

  continueText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "700",
  },
});

export default MobileNumberEntryScreen;
